/* eslint-disable react/no-unstable-nested-components */
import clsx from 'clsx';
import React, { useCallback, useEffect, useRef } from 'react';
import { useVisibilityState } from '../../hooks/useVisibilityState';
import { useAiStore } from '../../stores/AiStore';
import { SmartsContent } from './SmartsContent';
import { SmartsErrorBoundary } from './SmartsErrorBoundary';
import { stores } from '../../stores';
import AILogoLight from '../../assets/ai/logo.ai.light.svg';

export function Smarts() {
	const { isPinned, setIsPinned, isOpen, setIsOpen, pinnedWidth, setPinnedWidth } = useAiStore();
	const [visible, setVisible, shouldRender] = useVisibilityState(isOpen, 300);
	const input = useRef<HTMLInputElement>(null);

	const handleSetVisible = useCallback((state: boolean) => {
		setVisible(state);
		setIsOpen(state);
	}, [setIsOpen, setVisible]);

	// Ensure visibility state stays in sync with isOpen from AiStore
	useEffect(() => {
		setVisible(isOpen);
	}, [isOpen, setVisible]);

	useEffect(() => {
		if (visible) {
			input.current?.focus();
		}
	}, [visible, input]);

	// Resize in pinned mode
	const aiPanelRef = useRef<HTMLDivElement>(null);
	const handleMouseDown = useCallback((e: React.MouseEvent) => {
		if (e.button !== 0) {
			return;
		}
		e.preventDefault();
		const startX = e.clientX;
		const startWidth = pinnedWidth;

		if (!aiPanelRef.current) {
			return;
		}

		const mainPlaceholder = document.getElementById('main-ai-placeholder');
		// Disable transitions while dragging
		aiPanelRef.current!.style.transitionProperty = 'none';
		if (mainPlaceholder) {
			mainPlaceholder.style.transitionProperty = 'none';
		}

		const handle = e.target as HTMLElement;
		handle.style.setProperty('--ai-splitter-opacity', '1');

		let rafId: number | null = null;
		const handleMouseMove = (moveEvent: MouseEvent) => {
			if (rafId) {
				cancelAnimationFrame(rafId);
			}

			rafId = requestAnimationFrame(() => {
				const delta = startX - moveEvent.clientX;
				const newWidth = Math.max(300, Math.min(1500, startWidth + delta));
				// Update both the AI panel and the main placeholder
				aiPanelRef.current.style.setProperty('--ai-width', `${newWidth}px`);
				mainPlaceholder.style.setProperty('--ai-width', `${newWidth}px`);
			});
		};

		const handleMouseUp = () => {
			document.removeEventListener('mousemove', handleMouseMove);
			document.removeEventListener('mouseup', handleMouseUp);
			if (rafId) {
				cancelAnimationFrame(rafId);
			}

			const currentWidth = parseInt(getComputedStyle(aiPanelRef.current!)
				.getPropertyValue('--ai-width'), 10);
			setPinnedWidth(currentWidth);

			// Re-enable transitions
			aiPanelRef.current!.style.transitionProperty = '';
			if (mainPlaceholder) {
				mainPlaceholder.style.transitionProperty = '';
			}

			handle.style.setProperty('--ai-splitter-opacity', '0');
		};

		document.addEventListener('mousemove', handleMouseMove);
		document.addEventListener('mouseup', handleMouseUp);
	}, [pinnedWidth, setPinnedWidth]);

	const handleReset = () => {
		// Clear current thread id in case there was something in it that caused the error
		localStorage.removeItem(`aiThread-${stores.identity.userId()}`);
	};

	return (
		<>
			{/* Show/hide button */}
			<div
				className={clsx(
					'fixed bottom-0 right-0 z-(--z-aiButton) transition-all duration-300 ease-in-out w-[48px] h-[48px] mr-4 mb-4 overflow-hidden select-none cursor-pointer',
					'hover:w-[171px] rounded-full group',
					'bg-linear-(--gradient-ai-border) active:bg-linear-(--gradient-ai-border-pressed)',
					visible ? 'opacity-0' : 'opacity-100',
				)}
				onClick={() => handleSetVisible(true)}
				onKeyDown={(e) => e.key === 'Enter' && handleSetVisible(true)}
				tabIndex={0}
				role="button"
				inert={visible ? '' : undefined}
			>
				<div className="absolute z-1 w-[48px] h-[48px] rounded-full bg-linear-(--gradient-ai-bg-with-white-icon) group-hover:bg-linear-(--gradient-ai-bg-with-white-icon-hover) flex items-center justify-center cursor-pointer shrink-0 transition-colors duration-300">
					<img src={AILogoLight} alt="Relevant Logo" className="size-[30px] object-contain" />
				</div>
				<div className="w-full h-full p-[2px]">
					<div className="bg-white rounded-full flex items-center w-full h-full overflow-hidden">
						<div className="flex items-center -translate-x-full group-hover:translate-x-0 transition-all duration-300 ease-in-out ml-[57px]">
							<span className="font-semibold text-lg shrink-0 opacity-0 group-hover:opacity-100 bg-linear-(--gradient-ai-text-after-purple-icon) group-active:bg-linear-(--gradient-ai-text-after-purple-icon-pressed) text-transparent bg-clip-text transition-opacity duration-300">Relevant AI</span>
						</div>
					</div>
				</div>
			</div>

			{/* Chat container */}
			{shouldRender && (
				<div
					className={clsx(
						'fixed bottom-0 right-0 z-[var(--z-aiWindow)] pointer-events-none',
					)}
				>
					<div
						className={clsx(
							'border-grey-100 border box-border',
							'transition-all duration-300 ease-in-out',
							'flex flex-col bg-white relative pointer-events-auto',
							isPinned ? 'h-[100vh] border-t-0 border-r-0 border-b-0' : 'w-[min(600px,calc(100vw-32px-16px))] h-[min(700px,calc(100vh-32px-var(--topbar-height)))] m-4 rounded-lg',
							// Always fullscreen on mobile
							// 'max-lg:w-[100vw]! max-lg:h-[100vh]! max-lg:m-0! max-lg:rounded-none! max-lg:transition-none! max-lg:border-none!',
							visible
								? 'opacity-100 shadow-lg'
								: 'opacity-0 translate-y-6',
						)}
						style={{
							width: isPinned ? `var(--ai-width)` : undefined,
							['--ai-width' as string]: `${pinnedWidth}px`,
						}}
						ref={aiPanelRef}
						inert={!visible ? '' : undefined}
					>
						{/* Splitter handle */}
						{isPinned && (
							// eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
							<div
								className={clsx(
									'absolute z-1102 left-[-5px] top-0 bottom-0 w-[10px] cursor-col-resize',
									// Cool gradient effect that shows the handle
									'before:absolute before:inset-0',
									'before:opacity-(--ai-splitter-opacity) hover:before:opacity-100',
									'before:transition-opacity before:duration-200',
									'before:bg-linear-to-r before:from-blue-500/20 before:via-blue-500/10 before:to-transparent',
									'before:backdrop-blur-xs',
								)}
								style={{ '--ai-splitter-opacity': '0' } as React.CSSProperties}
								role="separator"
								onMouseDown={handleMouseDown}
							/>
						)}

						<SmartsErrorBoundary onReset={handleReset}>
							{shouldRender && (
								<SmartsContent visible={visible} setVisible={handleSetVisible} />
							)}
						</SmartsErrorBoundary>
					</div>
				</div>
			)}
		</>
	);
}
